import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Title,
  Text,
  Card,
  Group,
  Stack,
  Button,
  TextInput,
  Textarea,
  Switch,
  Alert,
  Badge,
  UnstyledButton,
  Avatar,
  FileButton,
  Loader,
  Chip,
  SimpleGrid,
} from '@mantine/core';
import { IconHeart } from '@tabler/icons-react';
import {
  IconAlertCircle,
  IconBan,
  IconUser,
  IconLock,
  IconChevronRight,
  IconMail,
  IconBrandGoogle,
  IconBrandApple,
  IconBell,
  IconTrash,
  IconCircleCheck,
  IconCamera,
  IconLogout,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useMutation } from '@tanstack/react-query';
import { useAuth } from '../../context/AuthContext';
import { usersService } from '../../services/users';
import { INTEREST_CATEGORIES } from '../../utils/categories';

interface SettingsRowProps {
  icon: React.ReactNode;
  label: string;
  description?: string;
  onClick?: () => void;
  rightSection?: React.ReactNode;
  disabled?: boolean;
}

function SettingsRow({ icon, label, description, onClick, rightSection, disabled }: SettingsRowProps) {
  return (
    <UnstyledButton
      onClick={onClick}
      py="sm"
      px="md"
      disabled={disabled}
      style={(theme) => ({
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        borderRadius: theme.radius.sm,
        opacity: disabled ? 0.5 : 1,
        cursor: disabled ? 'default' : 'pointer',
      })}
    >
      <Group gap="sm" style={{ flex: 1 }}>
        {icon}
        <div>
          <Text size="sm" fw={500}>{label}</Text>
          {description && <Text size="xs" c="dimmed">{description}</Text>}
        </div>
      </Group>
      {rightSection || <IconChevronRight size={16} color="var(--mantine-color-dimmed)" />}
    </UnstyledButton>
  );
}

export default function Settings() {
  const navigate = useNavigate();
  const { user, updateUser, resendVerificationEmail, logout } = useAuth();

  // Profile form state
  const [displayName, setDisplayName] = useState(user?.displayName || user?.username || '');
  const [bio, setBio] = useState(user?.bio || '');
  const [isPrivate, setIsPrivate] = useState(user?.isPrivate || false);
  const [interests, setInterests] = useState<string[]>(user?.categoryInterests ?? []);

  useEffect(() => {
    if (user) {
      setDisplayName(user.displayName || user.username || '');
      setBio(user.bio || '');
      setIsPrivate(user.isPrivate);
      setInterests(user.categoryInterests ?? []);
    }
  }, [user]);

  const saveProfileMutation = useMutation({
    mutationFn: () => usersService.updateProfile({
      displayName: displayName.trim() || undefined,
      bio: bio.trim() || undefined,
      isPrivate,
    }),
    onSuccess: (updatedUser) => {
      updateUser(updatedUser);
      notifications.show({ title: 'Saved', message: 'Profile updated successfully', color: 'green' });
    },
    onError: () => {
      notifications.show({ title: 'Error', message: 'Failed to update profile', color: 'red' });
    },
  });

  const saveInterestsMutation = useMutation({
    mutationFn: (cats: string[]) => usersService.updateProfile({ categoryInterests: cats }),
    onSuccess: (updatedUser) => {
      updateUser(updatedUser);
      notifications.show({ title: 'Saved', message: 'Interests updated', color: 'green' });
    },
    onError: () => {
      notifications.show({ title: 'Error', message: 'Failed to update interests', color: 'red' });
    },
  });

  const imageUploadMutation = useMutation({
    mutationFn: (file: File) => usersService.uploadProfileImage(file),
    onSuccess: (updatedUser) => {
      updateUser(updatedUser);
      notifications.show({ title: 'Updated', message: 'Profile picture updated', color: 'green' });
    },
    onError: (err) => {
      const msg = err instanceof Error ? err.message : 'Failed to upload image';
      notifications.show({ title: 'Upload failed', message: msg, color: 'red' });
    },
  });

  const resendVerificationMutation = useMutation({
    mutationFn: () => resendVerificationEmail(),
    onSuccess: () => {
      notifications.show({ title: 'Sent', message: 'Verification email sent. Check your inbox.', color: 'green' });
    },
    onError: () => {
      notifications.show({ title: 'Error', message: 'Failed to send verification email', color: 'red' });
    },
  });

  const handleImageUpload = (file: File | null) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      notifications.show({ title: 'Error', message: 'Please select an image file', color: 'red' });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      notifications.show({ title: 'Error', message: 'Image must be under 5MB', color: 'red' });
      return;
    }
    imageUploadMutation.mutate(file);
  };

  if (!user) {
    return (
      <Container size="sm" pt="md" pb="xl">
        <Alert icon={<IconAlertCircle size={16} />} color="red" title="Not logged in">
          You must be logged in to view settings.
        </Alert>
      </Container>
    );
  }

  return (
    <Container size="sm" pt="md" pb="xl">
      <Title order={2} mb="lg">
        Settings
      </Title>

      {/* Email Verification Banner */}
      {!user.isEmailVerified && (
        <Alert
          icon={<IconMail size={16} />}
          color="yellow"
          title="Email not verified"
          mb="lg"
        >
          <Text size="sm" mb="xs">
            Please verify your email address ({user.email}) to secure your account.
          </Text>
          <Button
            size="xs"
            variant="light"
            color="yellow"
            loading={resendVerificationMutation.isPending}
            onClick={() => resendVerificationMutation.mutate()}
          >
            Resend Verification Email
          </Button>
        </Alert>
      )}

      {/* Profile Section */}
      <Card shadow="sm" padding="lg" radius="md" withBorder mb="lg">
        <Group gap="xs" mb="md">
          <IconUser size={20} />
          <Title order={4}>Profile</Title>
        </Group>

        <Stack gap="md">
          <Group justify="center">
            <FileButton onChange={handleImageUpload} accept="image/*">
              {(props) => (
                <UnstyledButton {...props} style={{ position: 'relative' }}>
                  <Avatar
                    src={user?.profileImageUrl}
                    alt={user?.username}
                    size={100}
                    radius="xl"
                  >
                    <IconUser size={40} />
                  </Avatar>
                  <div
                    style={{
                      position: 'absolute',
                      bottom: 0,
                      right: 0,
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      backgroundColor: 'var(--mantine-color-blue-filled)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '2px solid var(--mantine-color-body)',
                    }}
                  >
                    {imageUploadMutation.isPending ? (
                      <Loader size={14} color="white" />
                    ) : (
                      <IconCamera size={16} color="white" />
                    )}
                  </div>
                </UnstyledButton>
              )}
            </FileButton>
          </Group>

          <TextInput
            label="Username"
            value={user?.username || ''}
            disabled
            description="Username cannot be changed"
          />

          <TextInput
            label="Display Name"
            value={displayName}
            onChange={(e) => setDisplayName(e.currentTarget.value)}
            maxLength={30}
          />

          <Textarea
            label="Bio"
            value={bio}
            onChange={(e) => setBio(e.currentTarget.value)}
            maxLength={160}
            autosize
            minRows={2}
            maxRows={4}
          />

          <Switch
            label="Private account"
            description="Only approved followers can see your reviews"
            checked={isPrivate}
            onChange={(e) => setIsPrivate(e.currentTarget.checked)}
          />

          <Button onClick={() => saveProfileMutation.mutate()} loading={saveProfileMutation.isPending}>
            Save Changes
          </Button>
        </Stack>
      </Card>

      {/* Interests */}
      <Card shadow="sm" padding="lg" radius="md" withBorder mb="lg">
        <Group gap="xs" mb="xs">
          <IconHeart size={20} />
          <Title order={4}>Interests</Title>
        </Group>
        <Text size="sm" c="dimmed" mb="md">
          What kinds of product reviews are you interested in? This personalises your discover feed.
        </Text>
        <SimpleGrid cols={3} spacing="xs" mb="md">
          {INTEREST_CATEGORIES.map(({ label, emoji }) => (
            <Chip
              key={label}
              checked={interests.includes(label)}
              onChange={() =>
                setInterests((prev) =>
                  prev.includes(label) ? prev.filter((l) => l !== label) : [...prev, label]
                )
              }
              radius="xl"
              size="sm"
              styles={{ label: { width: '100%', justifyContent: 'center' } }}
            >
              {emoji} {label}
            </Chip>
          ))}
        </SimpleGrid>
        <Button
          onClick={() => saveInterestsMutation.mutate(interests)}
          loading={saveInterestsMutation.isPending}
        >
          Save Interests
        </Button>
      </Card>

      {/* Account & Security */}
      <Card shadow="sm" padding="md" radius="md" withBorder mb="lg">
        <Title order={5} c="dimmed" tt="uppercase" fz="xs" fw={700} px="md" pt="xs" pb="sm">
          Account & Security
        </Title>
        <Stack gap={0}>
          <SettingsRow
            icon={<IconMail size={20} color="var(--mantine-color-blue-6)" />}
            label="Email"
            description={user.email}
            rightSection={
              user.isEmailVerified
                ? <Badge color="green" variant="light" size="sm" leftSection={<IconCircleCheck size={12} />}>Verified</Badge>
                : <Badge color="yellow" variant="light" size="sm">Unverified</Badge>
            }
          />
          <SettingsRow
            icon={<IconLock size={20} color="var(--mantine-color-blue-6)" />}
            label="Change Password"
            description="Update your account password"
            onClick={() => navigate('/settings/password')}
          />
          <SettingsRow
            icon={<IconBan size={20} color="var(--mantine-color-red-6)" />}
            label="Blocked Users"
            description="Manage users you've blocked"
            onClick={() => navigate('/settings/blocked')}
          />
        </Stack>
      </Card>

      {/* Connected Accounts */}
      <Card shadow="sm" padding="md" radius="md" withBorder mb="lg">
        <Title order={5} c="dimmed" tt="uppercase" fz="xs" fw={700} px="md" pt="xs" pb="sm">
          Connected Accounts
        </Title>
        <Stack gap={0}>
          <SettingsRow
            icon={<IconBrandGoogle size={20} color="var(--mantine-color-red-6)" />}
            label="Google"
            description="Link your Google account for quick sign-in"
            disabled
            rightSection={<Badge color="gray" variant="light" size="sm">Coming Soon</Badge>}
          />
          <SettingsRow
            icon={<IconBrandApple size={20} />}
            label="Apple"
            description="Link your Apple account for quick sign-in"
            disabled
            rightSection={<Badge color="gray" variant="light" size="sm">Coming Soon</Badge>}
          />
        </Stack>
      </Card>

      {/* Notifications */}
      <Card shadow="sm" padding="md" radius="md" withBorder mb="lg">
        <Title order={5} c="dimmed" tt="uppercase" fz="xs" fw={700} px="md" pt="xs" pb="sm">
          Notifications
        </Title>
        <Stack gap={0}>
          <SettingsRow
            icon={<IconBell size={20} color="var(--mantine-color-violet-6)" />}
            label="Notification Preferences"
            description="Manage email and push notification settings"
            disabled
            rightSection={<Badge color="gray" variant="light" size="sm">Coming Soon</Badge>}
          />
        </Stack>
      </Card>

      {/* Danger Zone */}
      <Card shadow="sm" padding="md" radius="md" withBorder style={{ borderColor: 'var(--mantine-color-red-3)' }}>
        <Title order={5} c="red" tt="uppercase" fz="xs" fw={700} px="md" pt="xs" pb="sm">
          Danger Zone
        </Title>
        <Stack gap={0}>
          <SettingsRow
            icon={<IconLogout size={20} color="var(--mantine-color-red-6)" />}
            label="Sign Out"
            description="Sign out of your account on this device"
            onClick={() => logout()}
          />
          <SettingsRow
            icon={<IconTrash size={20} color="var(--mantine-color-red-6)" />}
            label="Delete Account"
            description="Permanently delete your account and all data"
            onClick={() => navigate('/settings/delete-account')}
          />
        </Stack>
      </Card>
    </Container>
  );
}
